import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Target, Activity, GitCommit, ChevronDown, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import PortfolioProfileToggle from '../components/PortfolioProfileToggle';
import { API_BASE_URL as API_BASE } from '../apiBase';
import CalendarTab from './assessment/CalendarTab';
import MonteCarloTab from './assessment/MonteCarloTab';
import ReturnsTab from './assessment/ReturnsTab';
import ExcursionTab from './assessment/ExcursionTab';
import IcTab from './assessment/IcTab';
import PearsonTab from './assessment/PearsonTab';
import WindowsTab from './assessment/WindowsTab';
import WinRateTab from './assessment/WinRateTab';
import TakeProfitTab from './assessment/TakeProfitTab';
import { SectionHeader } from './assessment/shared';
import {
  AssessmentDataProvider, useAssessmentData, TABS, DTE_STRATEGIES,
} from './assessment/AssessmentDataContext';

// Every tab's own fetch/derived state, table primitives, and per-tab
// constants (TP_CALL_BE/TP_PUT_BE, TP_ANCHORS, WinRateTable, BucketPill,
// DeltaBadge, the color helpers, CALL_BUCKETS/PUT_BUCKETS) now live in
// ./assessment/shared.js, ./assessment/AssessmentDataContext.js, and each
// tab's own file under ./assessment/*Tab.js — see the imports above.
// Assessment.js itself only owns the page chrome: header, version selector,
// DTE/profile toggles, tab bar, and side filters.


// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtVersionDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

// fmtPct / MIN_RELIABLE_N / tpRateColor / WINRATE_GLOW_RGB / winRateGlowStyle /
// captureColor / icColor / corrColor / rtrColor / BucketPill / DeltaBadge /
// WinRateTable all moved into ./assessment/shared.js (Tech Debt 2026-09,
// "Assessment and Backtest are god files" — remaining-7-tabs context lift):
// every one of them is used by 2+ of the tabs still inline below.

function MetricTierGuide() {
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage?.getItem('assessmentMetricGuideOpen') === '1';
  });
  const toggle = () => {
    setOpen(prev => {
      const next = !prev;
      try { window.localStorage?.setItem('assessmentMetricGuideOpen', next ? '1' : '0'); } catch (e) {}
      return next;
    });
  };
  const tiers = [
    {
      name: 'S',
      color: 'text-trading-green-300 border-trading-green-400/40 bg-trading-green-400/[0.06]',
      header: 'Use for ship decisions',
      items: [
        { label: 'MC 5y WorstDD (N≥500)', detail: 'Real DD floor; survives translation to live execution. Stage 3 T4 ship gate.' },
        { label: 'MC P(collapse) per cell',  detail: 'Existential check — must be 0% across all (window × mode). Stage 3 T6 ship gate.' },
        { label: 'Per-trade TP% / WR15 by tier', detail: 'Hard to fake, doesn\'t compound. Found on the Win Rates and TP% tabs. Stage 1 W1 ship gate.' },
      ],
    },
    {
      name: 'A',
      color: 'text-sky-300 border-sky-400/30 bg-sky-400/[0.05]',
      header: 'Essential supporting',
      items: [
        { label: 'MC per-window MedRet (per year)', detail: 'One-year compound is bounded enough to be realizable. Detects regime-dependent failures.' },
        { label: 'MC per-window WorstDD',           detail: 'Stage 3 T5 — catches DD asymmetries the 5y aggregate hides.' },
        { label: 'Per-trade avg_option_pnl (TP%)',  detail: 'Theta-adjusted realized P&L. Catches "TP fires late under heavy decay" cases.' },
        { label: 'Per-tier N stability',            detail: 'A scoring change that "improves" WR by collapsing N is filtering, not alpha.' },
      ],
    },
    {
      name: 'B',
      color: 'text-gray-300 border-white/[0.08] bg-white/[0.02]',
      header: 'Useful, secondary',
      items: [
        { label: 'MC P25 / P75 compound', detail: 'Distribution shape. At long horizons, tails extend past realizable execution cap.' },
        { label: 'Deterministic 1-path WorstDD', detail: 'One realized path. DD survives execution, but single-sample variance is large at this compound scale.' },
        { label: 'MC 22-now compound', detail: 'Confirmation only. N=150 22-now wins reverse at 5y N=300 (Phase OP1 lesson).' },
      ],
    },
    {
      name: 'F',
      color: 'text-trading-red-300 border-trading-red-400/30 bg-trading-red-400/[0.05]',
      header: 'Misleading — don\'t ship on these',
      items: [
        { label: 'Calendar tab "avg return/yr"', detail: 'Single realized path, single realization of MC noise. At 1e15+%/yr it\'s past execution cap.' },
        { label: 'Mean of MC compound paths', detail: 'Lognormal skew — mean dominated by best tails, orders of magnitude above where 90% of paths land.' },
        { label: 'Cross-version compound w/o DD', detail: 'Compound is theoretical above 1e10%. Two versions can show 5× compound differences that are pure path noise.' },
      ],
    },
  ];
  return (
    <div className="bg-trading-dark-900 border border-white/[0.06] rounded-md overflow-hidden">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.02] transition-colors text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-gray-400">
            Metric tier guide
          </span>
          <span className="text-[11px] text-gray-600 truncate">
            which numbers actually drive ship decisions vs which are theoretical
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-white/[0.06] px-4 py-3 space-y-3">
          <p className="text-[12px] text-gray-400 leading-relaxed">
            Two distinct measurement systems live in this codebase: the per-trade
            barrier-touch assessment (cheap, deterministic, decomposes by tier — what these
            tabs show) and the canonical Monte Carlo at N=500 × 8 windows
            (expensive, distributional — only persisted in <span className="font-mono text-gray-300">version-history.md</span>).
            Both are valid; they answer different questions.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {tiers.map(t => (
              <div key={t.name} className={`border ${t.color} rounded px-3 py-2.5 space-y-1.5`}>
                <div className="flex items-center gap-2">
                  <span className={`text-[12px] font-bold font-mono w-5 text-center`}>{t.name}</span>
                  <span className="text-[11px] font-medium uppercase tracking-[0.14em]">{t.header}</span>
                </div>
                <ul className="space-y-1 pl-1">
                  {t.items.map(it => (
                    <li key={it.label} className="text-[11px] leading-relaxed">
                      <span className="text-gray-200 font-medium">{it.label}</span>
                      <span className="text-gray-500"> — {it.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-gray-600 leading-relaxed pt-1 border-t border-white/[0.04]">
            <span className="text-gray-500">Practical workflow:</span> for day-to-day reading, use Win Rates / TP% per
            tier (S-tier per-trade quality). For ship decisions, run <span className="font-mono text-gray-400">monte_carlo.py</span>
            {' '}at N=500 × 8 windows and read 5y WorstDD + P(collapse) from stdout. The Calendar tab is informational only.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-trading-dark-900 border border-white/[0.06] rounded-md px-4 py-3 flex items-start gap-3">
      <div className="mt-0.5 flex-shrink-0">
        <Icon className="w-3.5 h-3.5 text-gray-500" strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 mb-1">{label}</p>
        <p className="text-[15px] font-semibold text-white font-mono tabular-nums">{value}</p>
        {sub && <p className="text-[11px] text-gray-500 mt-0.5 truncate">{sub}</p>}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const Assessment = () => {
  const {
    activeTab, setActiveTab,
    data, loading, error,
    availableVersions, selectedVersionId, setSelectedVersionId,
    refreshNonce,
    showCalls, setShowCalls, showPuts, setShowPuts, showScaled, setShowScaled,
    dteStrategy, setDteStrategyPersist, dteCfg,
    portfolioProfile, setPortfolioProfilePersist, portfolioProfiles, updatePortfolioProfiles,
    selectedWindow, setSelectedWindow,
    versionMenuOpen, setVersionMenuOpen, versionMenuRef,
  } = useAssessmentData();

  const versionMenuEl = availableVersions.length > 0 && (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setVersionMenuOpen(o => !o); }}
        className="absolute inset-0 w-full h-full cursor-pointer"
        aria-label="Select algorithm version"
        aria-haspopup="listbox"
        aria-expanded={versionMenuOpen}
      />
      <ChevronDown className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${versionMenuOpen ? 'rotate-180' : ''}`} />
      {versionMenuOpen && (
        <div
          role="listbox"
          className="absolute right-0 top-full mt-1 z-20 min-w-[260px] max-h-80 overflow-y-auto bg-trading-dark-900 border border-white/[0.08] rounded-md shadow-xl py-1"
          onClick={(e) => e.stopPropagation()}
        >
          {availableVersions.map(v => {
            const isSelected = v.id === selectedVersionId;
            return (
              <button
                key={v.id}
                role="option"
                aria-selected={isSelected}
                onClick={() => { setSelectedVersionId(v.id); setVersionMenuOpen(false); }}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-white/[0.03] transition-colors ${
                  isSelected ? 'bg-white/[0.04]' : ''
                }`}
              >
                <Check className={`w-3.5 h-3.5 flex-shrink-0 ${isSelected ? 'text-trading-green-300' : 'text-transparent'}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-mono text-white truncate tabular-nums">
                    v{v.id}
                    {v.git_commit && (
                      <span className="text-gray-500 ml-1">{v.git_commit.slice(0, 7)}</span>
                    )}
                    {v.created_at && (
                      <span className="text-[11px] text-gray-600 ml-2 font-sans">{fmtVersionDate(v.created_at)}</span>
                    )}
                    {v.is_active && (
                      <span className="text-[10px] uppercase tracking-[0.14em] text-trading-green-300/90 ml-2">active</span>
                    )}
                  </p>
                  {v.git_message && (
                    <p className="text-xs text-gray-500 truncate">{v.git_message}</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </>
  );

  // Only show full-page spinner on the very first load (no data yet).
  // Subsequent refetches (e.g. DTE toggle) keep the previous data visible
  // so there's no flash — the values just swap when the new fetch lands.
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Activity className="w-5 h-5 animate-spin mr-2" />
        Loading assessment data…
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-4 gap-3">
        <p className="text-trading-red-400 font-medium">Failed to load assessment</p>
        <p className="text-gray-500 text-sm">{error}</p>
        {availableVersions.length > 0 && (
          <div className="flex flex-col items-center gap-2 mt-2">
            <p className="text-xs text-gray-500">Try a different version:</p>
            <div ref={versionMenuRef} className="relative bg-trading-dark-900 border border-white/[0.08] hover:border-white/[0.14] rounded-md px-4 py-2 text-sm text-white font-mono tabular-nums flex items-center gap-3 transition-colors">
              Select version
              {versionMenuEl}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!data) return null;

  const { version, buckets } = data;
  const totalCallPeaks = buckets.filter(b => b.side === 'call').reduce((s, b) => s + b.sample_count, 0);
  const totalPutPeaks  = buckets.filter(b => b.side === 'put').reduce((s, b) => s + b.sample_count, 0);

  const holdP = dteCfg.holdPeriod;
  const bestCall = buckets.filter(b => b.side === 'call').reduce((best, b) => {
    const wr = b.win_rate?.[holdP];
    if (wr == null) return best;
    return (!best || wr > best.win_rate?.[holdP]) ? b : best;
  }, null);
  const bestPut = buckets.filter(b => b.side === 'put').reduce((best, b) => {
    const wr = b.win_rate?.[holdP];
    if (wr == null) return best;
    return (!best || wr > best.win_rate?.[holdP]) ? b : best;
  }, null);

  return (
    <div className="h-full overflow-y-auto bg-trading-dark-950">
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">

        {/* Page header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <h1 className="text-[11px] font-medium uppercase tracking-[0.18em] text-gray-500">Assessment</h1>
            <p className="text-[22px] font-semibold text-white tracking-tight mt-1">
              Backtest outcomes
            </p>
            <p className="text-[12px] text-gray-500 mt-1">
              {version.id === data.active_version_id ? 'Active' : 'Selected'} algorithm version
            </p>
          </div>

          {/* DTE strategy toggle — fixed-width buttons + nowrap to prevent any layout shift */}
          <div className="flex items-center bg-trading-dark-900 border border-white/[0.08] rounded-md p-0.5">
            {Object.entries(DTE_STRATEGIES).map(([k, cfg]) => (
              <button
                key={k}
                type="button"
                onClick={() => setDteStrategyPersist(k)}
                className={`px-3 py-1.5 text-[11px] font-medium rounded w-[72px] text-center whitespace-nowrap ${
                  dteStrategy === k
                    ? 'bg-trading-blue-600/20 text-trading-blue-300'
                    : 'text-gray-500'
                }`}
                title={`Show ${cfg.label} strategy anchors (WR${cfg.hardPeriod.replace('d','')} hard sell + WR${cfg.holdPeriod.replace('d','')} end hold)`}
              >
                {cfg.label}
              </button>
            ))}
          </div>

          {/* Version badges */}
          {(() => {
            // availableVersions is sorted by id desc. Find neighbors of current.
            const idx = availableVersions.findIndex(v => v.id === version.id);
            const older = idx >= 0 ? availableVersions[idx + 1] : null;
            const newer = idx > 0 ? availableVersions[idx - 1] : null;
            const navBadge = (v, direction) => (
              <button
                key={direction}
                type="button"
                onClick={() => setSelectedVersionId(v.id)}
                className="bg-trading-dark-900 border border-white/[0.06] hover:border-white/[0.14] rounded-md px-3 py-2 flex items-center gap-3 min-w-0 opacity-60 hover:opacity-100 transition-colors text-left"
                title={`Go to v${v.id}`}
              >
                {direction === 'next' && <ChevronLeft className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />}
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600">{direction === 'prev' ? 'prev' : 'next'} v{v.id}</p>
                  <p className="text-[12px] font-mono tabular-nums text-gray-400 mt-0.5">
                    {v.git_commit?.slice(0, 7) ?? '—'}
                    {v.created_at && (
                      <span className="text-[11px] text-gray-600 ml-2 font-sans">{fmtVersionDate(v.created_at)}</span>
                    )}
                  </p>
                  {v.git_message && (
                    <p className="text-[11px] text-gray-600 truncate max-w-[180px] mt-0.5">{v.git_message}</p>
                  )}
                </div>
                {direction === 'prev' && <ChevronRight className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />}
              </button>
            );
            return (
              <div className="flex items-stretch gap-2 flex-wrap justify-end ml-auto">
                {newer && navBadge(newer, 'next')}
                <div ref={versionMenuRef} className="relative bg-trading-dark-900 border border-white/[0.08] hover:border-white/[0.14] rounded-md px-3 py-2 flex items-center gap-3 min-w-0 transition-colors">
                  <GitCommit className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.14em] text-gray-500">
                      {version.id === data.active_version_id ? 'Active version' : 'Historical version'}
                    </p>
                    <p className="text-[12px] font-mono tabular-nums text-white mt-0.5">
                      v{version.id}
                      {version.git_commit && (
                        <span className="text-gray-500 ml-1">{version.git_commit.slice(0, 7)}</span>
                      )}
                      {version.created_at && (
                        <span className="text-[11px] text-gray-500 ml-2 font-sans">{fmtVersionDate(version.created_at)}</span>
                      )}
                    </p>
                    {version.git_message && (
                      <p className="text-[11px] text-gray-500 truncate max-w-[220px] mt-0.5">{version.git_message}</p>
                    )}
                  </div>
                  {versionMenuEl}
                </div>
                {older && navBadge(older, 'prev')}
              </div>
            );
          })()}
        </div>

        {data.active_version_id != null && version.id !== data.active_version_id && (
          <p className="text-[11px] text-amber-300/80 -mt-2 pl-2 border-l-2 border-amber-500/40">
            Viewing a historical version — some metrics may be missing if they weren't computed at that time.
          </p>
        )}

        {/* ── Metric tier-list info — what's reliable vs theoretical ─────── */}
        <MetricTierGuide />

        {/* Stat cards — 2-col on mobile, 4-col on md+
            Lead with per-trade quality (S-tier metric) instead of raw peak counts.
            Cumulative call 75+ and put <25 WR at the strategy's hold period are
            the headline per-trade signals; everything else is supporting. */}
        {(() => {
          // Compute cumulative WR15 (or WR-holdP) for the cascade-relevant tiers.
          // Calls: 75+ is the cascade qualifying universe. Puts: <25 is qualifying.
          const cumCall = buckets.find(b => b.bucket === '75+' && b.side === 'call');
          const cumPut  = buckets.find(b => b.bucket === '<25' && b.side === 'put');
          const callWR  = cumCall?.win_rate?.[holdP];
          const putWR   = cumPut?.win_rate?.[holdP];
          const callN   = cumCall?.sample_count ?? 0;
          const putN    = cumPut?.sample_count ?? 0;
          const wrLabel = `WR${holdP.replace('d','')}`;
          return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard
                icon={TrendingUp}
                label={`Call 75+ ${wrLabel}`}
                value={callWR != null ? `${callWR.toFixed(1)}%` : '—'}
                sub={callN
                  ? `${callN.toLocaleString()} signals · best ${bestCall?.bucket} @ ${bestCall?.win_rate?.[holdP]?.toFixed(1)}%`
                  : 'no qualifying signals'}
              />
              <StatCard
                icon={TrendingDown}
                label={`Put <25 ${wrLabel}`}
                value={putWR != null ? `${putWR.toFixed(1)}%` : '—'}
                sub={putN
                  ? `${putN.toLocaleString()} signals · best ${bestPut?.bucket} @ ${bestPut?.win_rate?.[holdP]?.toFixed(1)}%`
                  : 'no qualifying signals'}
              />
              <StatCard
                icon={Activity}
                label="Total peaks"
                value={version.total_peaks?.toLocaleString() ?? '—'}
                sub={`${version.run_count} run${version.run_count !== 1 ? 's' : ''} · ${totalCallPeaks.toLocaleString()} calls / ${totalPutPeaks.toLocaleString()} puts`}
              />
              <StatCard
                icon={Target}
                label="Updated"
                value={version.updated_at
                  ? new Date(version.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '—'}
                sub={version.updated_at
                  ? new Date(version.updated_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                  : undefined}
              />
            </div>
          );
        })()}

        {/* Main table section */}
        <div className="bg-trading-dark-900 border border-white/[0.06] rounded-md overflow-hidden">

          {/* Toolbar — tabs on top, filters below on mobile; single row on sm+ */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-b border-white/[0.06]">
            {/* Tabs — underlined bar style, no pill chrome */}
            <div className="overflow-x-auto -mx-1 px-1">
              <div className="flex items-center gap-4 w-max">
                {TABS.map(tab => {
                  const active = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`relative py-1.5 text-[12px] transition-colors whitespace-nowrap ${
                        active
                          ? 'text-white font-medium'
                          : 'text-gray-500 hover:text-gray-300 font-normal'
                      }`}
                    >
                      {tab.label}
                      {active && (
                        <span className="absolute left-0 right-0 -bottom-[13px] h-[1px] bg-white" aria-hidden />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Side filters + win rate controls */}
            <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
              {/* Window selector — visible on every tab that consumes per-window
                  bucket data. Hidden on tabs that show all windows at once
                  (`windows`) or use a separate dataset (`calendar`, `ic`). */}
              {(data?.windows?.length > 0)
                && !['windows', 'calendar', 'ic', 'monte_carlo'].includes(activeTab) && (
                <div className="flex items-center border border-white/[0.06] rounded-md overflow-hidden">
                  {data.windows.map((w, i) => (
                    <button
                      key={w.label}
                      onClick={() => setSelectedWindow(w.label)}
                      className={`px-2.5 py-1 text-[11px] font-medium transition-colors whitespace-nowrap ${
                        i > 0 ? 'border-l border-white/[0.06]' : ''
                      } ${
                        w.label === selectedWindow
                          ? 'bg-white/[0.06] text-white'
                          : 'text-gray-500 hover:text-gray-300'
                      }`}
                    >
                      {w.label}
                    </button>
                  ))}
                </div>
              )}
              {/* Scaled / Unscaled toggle — only meaningful for win_rate */}
              {activeTab === 'win_rate' && (
                <button
                  onClick={() => setShowScaled(v => !v)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                    showScaled
                      ? 'border-white/[0.12] text-gray-200'
                      : 'border-amber-500/30 text-amber-300'
                  }`}
                >
                  {showScaled ? 'Scaled' : 'Unscaled'}
                </button>
              )}
              <button
                onClick={() => setShowCalls(v => !v)}
                className={`relative flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                  showCalls
                    ? 'border-white/[0.12] text-gray-200'
                    : 'border-white/[0.04] text-gray-600 hover:text-gray-400'
                }`}
              >
                <span className={`w-1 h-1 rounded-full ${showCalls ? 'bg-trading-green-400' : 'bg-gray-700'}`} />
                Calls
                {showCalls && <span className="absolute left-0 right-0 bottom-0 h-[2px] bg-trading-green-400/80" aria-hidden />}
              </button>
              <button
                onClick={() => setShowPuts(v => !v)}
                className={`relative flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                  showPuts
                    ? 'border-white/[0.12] text-gray-200'
                    : 'border-white/[0.04] text-gray-600 hover:text-gray-400'
                }`}
              >
                <span className={`w-1 h-1 rounded-full ${showPuts ? 'bg-trading-red-400' : 'bg-gray-700'}`} />
                Puts
                {showPuts && <span className="absolute left-0 right-0 bottom-0 h-[2px] bg-trading-red-400/80" aria-hidden />}
              </button>
            </div>
          </div>

          {/* Table content */}
          <div className="p-4">
            {activeTab === 'win_rate' && <WinRateTab />}

            {activeTab === 'take_profit' && <TakeProfitTab />}

            {activeTab === 'windows' && <WindowsTab />}

            {activeTab === 'returns' && <ReturnsTab />}

            {activeTab === 'excursion' && <ExcursionTab />}

            {activeTab === 'ic' && <IcTab />}

            {activeTab === 'calendar' && (
              <>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <SectionHeader>Backtest calendar — cascade allocation strategy</SectionHeader>
                  <PortfolioProfileToggle
                    profiles={portfolioProfiles}
                    value={portfolioProfile}
                    onChange={setPortfolioProfilePersist}
                    compact
                  />
                </div>
                <CalendarTab
                  apiBase={API_BASE}
                  versionId={selectedVersionId}
                  activeVersionId={data?.active_version_id}
                  dteStrategy={dteStrategy}
                  portfolioProfile={portfolioProfile}
                  onProfiles={updatePortfolioProfiles}
                  refreshNonce={refreshNonce}
                />
              </>
            )}

            {activeTab === 'monte_carlo' && (
              <>
                <SectionHeader>
                  Monte Carlo — N-iteration distribution per window — {dteCfg.label}
                </SectionHeader>
                <p className="text-[12px] text-gray-500 mb-4 leading-relaxed">
                  Canonical ship-gate metrics. Populated automatically when{' '}
                  <span className="font-mono text-gray-300">monte_carlo.py</span> /{' '}
                  <span className="font-mono text-gray-300">monte_carlo_15dte.py</span> run to completion. Each row is one
                  upsert per (version × dte × window × params_hash) — re-running the same config replaces the row.
                </p>
                <MonteCarloTab
                  apiBase={API_BASE}
                  dteStrategy={dteStrategy}
                  versionId={selectedVersionId}
                  refreshNonce={refreshNonce}
                />
              </>
            )}

            {activeTab === 'pearson' && <PearsonTab />}

          </div>
        </div>

      </div>
    </div>
  );
};

const AssessmentWithProvider = () => (
  <AssessmentDataProvider>
    <Assessment />
  </AssessmentDataProvider>
);

export default AssessmentWithProvider;
